import { Inject, Injectable } from '@angular/core';
import { Store } from '@sama/app-state';
import { isDefined } from '@sama/js-core';
import { InjectableSuperclass } from '@sama/ng-core';
import { BehaviorSubject, combineLatest, map, Observable, of } from 'rxjs';
import { environment } from '../../../../../../environments/environment';
import { getNumericSharedCssProperty } from '../../../../../outputs/annotation/annotation-shared/properties-shared-with-css';
import { AnnotationOutput } from '../../../../../team-apis/otterdam/outputs/annotation-output';
import { getFusionOutput } from '../../../../h3ml/dsl/sensor-fusion/sensor-fusion-output.utils';
import { FusionState } from '../../../../h3ml/state/fusion-state';
import {
  OutputInfo,
  outputInfoStoreToken,
} from '../../../../h3ml/state/output-info';
import {
  fusionStoreToken,
  TaskWorkspaceStore,
} from '../../../../h3ml/state/task-workspace-store';
import { getSharedFusionCssNumericProperty } from '../fusion/fusion-css-values';

// Represents the 15px padding of the workspace-outline
export const ANNOTATION_LAYOUT_MARGIN = 30;

@Injectable()
export class AnnotationLayoutService extends InjectableSuperclass {
  isTimelineHidden$: Observable<boolean>;
  isRightPanelHidden$: Observable<boolean>;

  private showTimeline$ = new BehaviorSubject(false);
  private outputName = this.outputInfoStore.state().output.name;
  private thumbnailContainerHeight = getSharedFusionCssNumericProperty(
    'output-thumbnail-container-height',
  );
  private thumbnailTitleHeight = getSharedFusionCssNumericProperty(
    'output-thumbnail-title-height',
  );
  private thumbnailWidth = getSharedFusionCssNumericProperty(
    'output-thumbnail-width',
  );

  constructor(
    @Inject(fusionStoreToken)
    private fusionStore: Store<FusionState>,
    @Inject(outputInfoStoreToken)
    private outputInfoStore: Store<OutputInfo<AnnotationOutput>>,
    private taskWorkspaceStore: TaskWorkspaceStore,
  ) {
    super();

    this.isTimelineHidden$ = combineLatest([
      this.getTimelineHeight$(),
      this.taskWorkspaceStore('splitViewOutputName').$,
    ]).pipe(
      map(
        ([height, splitViewOutputName]) =>
          height === 0 || isDefined(splitViewOutputName),
      ),
    );
    this.isRightPanelHidden$ = this.getRightPanelWidth$().pipe(
      map((width) => width === 0),
    );
  }

  displayTimeline() {
    // Should be part of Output Transient if ever we move away from layers.
    this.showTimeline$.next(true);
  }

  hideTimeline() {
    // Should be part of Output Transient if ever we move away from layers.
    this.showTimeline$.next(false);
  }

  getAnnotationHeight$() {
    // We use documentElement.clientHeight to be able to mock it.
    // Ideally we would use a dynamic value such as '95vh.
    // We need to use clientHeight and not the viewport to ignore
    // the different scrollbar width between CI and local (MAC) tests.
    // Idea: we could add watcher on window resize and update the value.
    const annotationHeight = Math.max(
      document.documentElement.clientHeight - ANNOTATION_LAYOUT_MARGIN,
      environment.outputMinHeight,
    );
    const thumbnailTabHeight =
      this.thumbnailContainerHeight + this.thumbnailTitleHeight;

    const project = this.taskWorkspaceStore.state().project;
    const fusionOutput = getFusionOutput(
      project,
      this.outputInfoStore.state().output.id,
    );
    if (!fusionOutput || fusionOutput.meta.fused_outputs.length <= 1) {
      return of(`${annotationHeight}px`);
    }

    return combineLatest([
      this.taskWorkspaceStore.isThumbnail$(this.outputName),
      this.taskWorkspaceStore('splitViewOutputName').$,
      this.getTimelineHeight$(),
    ]).pipe(
      map(([thumbnailMode, splitViewOutputName, timelineHeight]) => {
        if (isDefined(splitViewOutputName)) {
          return `${annotationHeight - timelineHeight}px`;
        }

        return thumbnailMode
          ? `${this.thumbnailContainerHeight}px`
          : `${annotationHeight - thumbnailTabHeight}px`;
      }),
    );
  }

  getAnnotationWidth$() {
    return this.taskWorkspaceStore
      .isThumbnail$(this.outputName)
      .pipe(
        map((thumbnailMode) =>
          thumbnailMode ? `${this.thumbnailWidth}px` : '100%',
        ),
      );
  }

  getRightPanelWidth$() {
    const annotationListWidth = this.taskWorkspaceStore.isUsingObjects()
      ? getNumericSharedCssProperty('object-list-width')
      : getNumericSharedCssProperty('shape-list-width');
    return combineLatest([
      this.fusionStore('isRightPanelExpanded').$,
      this.taskWorkspaceStore.isThumbnail$(this.outputName),
      this.taskWorkspaceStore('splitViewOutputName').$,
    ]).pipe(
      map(([isOpen, isInThumbnailMode, splitViewOutputName]) => {
        if (isDefined(splitViewOutputName) || !isOpen || isInThumbnailMode) {
          return 0;
        }
        return annotationListWidth;
      }),
    );
  }

  getTimelineHeight$() {
    const timelineHeight = getNumericSharedCssProperty('timeline-height');
    const focusedTimelineHeight = getNumericSharedCssProperty(
      'focused-timeline-height',
    );
    const fullTimelineHeight = timelineHeight + focusedTimelineHeight;

    return combineLatest([
      this.showTimeline$,
      this.taskWorkspaceStore.isThumbnail$(this.outputName),
    ]).pipe(
      map(([showTimeline, isInThumbnailMode]) => {
        if (!showTimeline || isInThumbnailMode) {
          return 0;
        }
        return fullTimelineHeight;
      }),
    );
  }
}
